import { useState, useMemo } from 'react';
import { Helmet } from 'react-helmet-async';
import {
  Bell,
  BellOff,
  Package,
  Tag,
  ShoppingBag,
  CheckCheck,
  ChevronRight,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import {
  useNotifications,
  useUnreadCount,
  useMarkAsRead,
  useMarkAllAsRead,
  useDeleteNotification,
} from '@/hooks/useNotification';
import { Notification, NotificationType } from '@bookstore/shared';
import { toast } from 'sonner';
import NotificationCard from '@/components/common/NotificationCard';

type FilterType = 'all' | 'unread' | NotificationType;

const NotificationsPage = () => {
  const { user } = useAuth();
  const [activeFilter, setActiveFilter] = useState<FilterType>('all');
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const { notifications, isLoading } = useNotifications(user?.uid);
  const { data: unreadCount } = useUnreadCount(user?.uid);
  const markAsReadMutation = useMarkAsRead();
  const markAllAsReadMutation = useMarkAllAsRead();
  const deleteNotificationMutation = useDeleteNotification();

  // Filter notifications
  const filteredNotifications = useMemo(() => {
    if (!notifications) return [];

    let filtered = notifications;

    if (activeFilter === 'unread') {
      filtered = notifications.filter((n) => !n.isRead);
    } else if (activeFilter !== 'all') {
      filtered = notifications.filter((n) => n.type === activeFilter);
    }

    return filtered;
  }, [notifications, activeFilter]);

  // Group by date
  const groupedNotifications = useMemo(() => {
    const groups: { [key: string]: Notification[] } = {
      Today: [],
      Yesterday: [],
      'This Week': [],
      Earlier: [],
    };

    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    const thisWeek = new Date(today);
    thisWeek.setDate(thisWeek.getDate() - 7);

    filteredNotifications.forEach((notification) => {
      const notifDate = new Date(notification.createdAt.toDate());
      const notifDay = new Date(notifDate.getFullYear(), notifDate.getMonth(), notifDate.getDate());

      if (notifDay.getTime() === today.getTime()) {
        groups.Today.push(notification);
      } else if (notifDay.getTime() === yesterday.getTime()) {
        groups.Yesterday.push(notification);
      } else if (notifDate >= thisWeek) {
        groups['This Week'].push(notification);
      } else {
        groups.Earlier.push(notification);
      }
    });

    return groups;
  }, [filteredNotifications]);

  // Handlers
  const handleToggleRead = (notification: Notification) => {
    markAsReadMutation.mutate(notification.id);
  };

  const handleDelete = (notificationId: string) => {
    setDeletingId(notificationId);
    setTimeout(() => {
      deleteNotificationMutation.mutate(notificationId, {
        onSuccess: () => {
          setDeletingId(null);
          toast.success('Notification deleted');
        },
        onError: () => {
          setDeletingId(null);
          toast.error('Failed to delete notification');
        },
      });
    }, 300);
  };

  const handleMarkAllAsRead = () => {
    if (!user?.uid) return;
    markAllAsReadMutation.mutate(user.uid);
  };

  // Filter tabs
  const filters: { value: FilterType; label: string; icon: any }[] = [
    { value: 'all', label: 'All', icon: Bell },
    { value: 'unread', label: 'Unread', icon: BellOff },
    { value: NotificationType.ORDER, label: 'Orders', icon: Package },
    { value: NotificationType.PROMOTION, label: 'Offers', icon: Tag },
    { value: NotificationType.BOOK, label: 'Books', icon: ShoppingBag },
  ];

  return (
    <>
      <Helmet>
        <title>Notifications | Sunrise Bookstore</title>
      </Helmet>

      <div className="min-h-screen bg-background-muted py-12">
        <div className="container-custom max-w-4xl">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h1 className="text-3xl font-extrabold tracking-tight text-text flex items-center gap-3">
                  <div className="relative">
                    <Bell className="w-8 h-8 text-primary-600" />
                    {(unreadCount || 0) > 0 && (
                      <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center shadow-lg animate-pulse">
                        {unreadCount! > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </div>
                  Notifications
                </h1>
                <p className="text-text-secondary mt-2">Stay updated with your orders and offers</p>
              </div>

              {(unreadCount || 0) > 0 && (
                <button
                  onClick={handleMarkAllAsRead}
                  disabled={markAllAsReadMutation.isPending}
                  className="
                    px-5 py-2.5 rounded-xl text-sm font-semibold
                    bg-gradient-to-r from-primary-600 to-primary-700 text-white
                    hover:from-primary-700 hover:to-primary-800
                    transition-all duration-300 hover:-translate-y-0.5
                    shadow-md shadow-primary-600/30 hover:shadow-lg
                    disabled:opacity-50 disabled:cursor-not-allowed
                    flex items-center gap-2
                  "
                >
                  <CheckCheck className="w-4 h-4" />
                  Mark All Read
                </button>
              )}
            </div>

            {/* Filter Tabs */}
            <div className="flex items-center gap-2 overflow-x-auto pb-2 scrollbar-hide">
              {filters.map((filter) => {
                const Icon = filter.icon;
                const isActive = activeFilter === filter.value;
                const count =
                  filter.value === 'all'
                    ? notifications?.length || 0
                    : filter.value === 'unread'
                      ? unreadCount || 0
                      : notifications?.filter((n) => n.type === filter.value).length || 0;

                return (
                  <button
                    key={filter.value}
                    onClick={() => setActiveFilter(filter.value)}
                    className={`
                      flex items-center gap-2 px-5 py-3 rounded-xl font-semibold text-sm
                      transition-all duration-300 whitespace-nowrap
                      ${
                        isActive
                          ? 'bg-gradient-to-r from-primary-600 to-primary-700 text-white shadow-lg shadow-primary-600/30 scale-105'
                          : 'bg-background-soft text-text-secondary hover:bg-background-subtle border border-border hover:border-primary-200 hover:scale-102'
                      }
                    `}
                  >
                    <Icon className="w-4 h-4" />
                    {filter.label}
                    {count > 0 && (
                      <span
                        className={`
                        px-2.5 py-0.5 rounded-full text-xs font-bold
                        ${isActive ? 'bg-white/20 text-white' : 'bg-primary-100 text-primary-700'}
                      `}
                      >
                        {count}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Loading State */}
          {isLoading ? (
            <div className="text-center py-24">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
              <p className="text-text-secondary">Loading notifications...</p>
            </div>
          ) : filteredNotifications.length === 0 ? (
            /* Empty State */
            <div className="bg-background-soft rounded-2xl shadow-md border border-border py-20 text-center">
              <div className="relative inline-block mb-6">
                <div className="absolute inset-0 bg-primary-600/10 blur-3xl rounded-full"></div>
                <BellOff className="w-20 h-20 text-primary-600/30 mx-auto relative" />
              </div>
              <h2 className="text-2xl font-bold text-text mb-2">
                {activeFilter === 'unread' ? 'All caught up!' : 'No notifications yet'}
              </h2>
              <p className="text-text-secondary mb-8 max-w-md mx-auto">
                {activeFilter === 'unread'
                  ? "You've read all your notifications. Great job staying on top of things!"
                  : "We'll notify you about orders, offers, and updates here."}
              </p>
              {activeFilter !== 'all' && (
                <button
                  onClick={() => setActiveFilter('all')}
                  className="
                    px-6 py-3 inline-flex items-center gap-2
                    bg-gradient-to-r from-primary-600 to-primary-700 text-white rounded-xl font-semibold
                    hover:from-primary-700 hover:to-primary-800
                    transition-all duration-300 hover:-translate-y-0.5 shadow-lg shadow-primary-600/30
                  "
                >
                  View All Notifications
                  <ChevronRight className="w-5 h-5" />
                </button>
              )}
            </div>
          ) : (
            /* Notifications List */
            <div className="space-y-8">
              {Object.entries(groupedNotifications).map(
                ([group, groupNotifications]) =>
                  groupNotifications.length > 0 && (
                    <div key={group} className="space-y-4">
                      {/* Group Header */}
                      <div className="flex items-center gap-3 px-2">
                        <h3 className="text-sm font-bold text-text-secondary uppercase tracking-wider">
                          {group}
                        </h3>
                        <div className="flex-1 h-px bg-gradient-to-r from-border to-transparent"></div>
                        <span className="text-xs font-semibold text-text-secondary/60">
                          {groupNotifications.length}
                        </span>
                      </div>

                      {/* Notifications in Group */}
                      <div className="space-y-4">
                        {groupNotifications.map((notification) => (
                          <NotificationCard
                            key={notification.id}
                            notification={notification}
                            isDeleting={deletingId === notification.id}
                            onToggleRead={handleToggleRead}
                            onDelete={handleDelete}
                            isMarkingAsRead={markAsReadMutation.isPending}
                            isDeletingNotification={deleteNotificationMutation.isPending}
                          />
                        ))}
                      </div>
                    </div>
                  )
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default NotificationsPage;